import { Icon, ProgressBar } from '@arcadiapower/shrike';
import { Container } from '~/components/layout';
import { Description } from '~/components/typography';
import { DeleteButton, ImageContainer, PreviewContainer } from '../image-uploader.style';

export interface ImagePreviewProps {
  onDelete: () => void;
  errorCode?: string;
  base64Image?: string;
  uploaded: boolean;
}

export const ImagePreview = ({ errorCode, onDelete, base64Image, uploaded }: ImagePreviewProps) => {
  const status = (uploaded && 'success') || (errorCode && 'error') || 'pending';
  const error = errorCode && (ERROR_CODES[errorCode] || ERROR_CODES['default-code']);

  return (
    <PreviewContainer testId="image-preview" mt={4}>
      <ImageContainer backgroundImage={base64Image ?? ''} mr={4} />
      {status === 'pending' && (
        <Container display="flex" flexDirection="column">
          <b>Uploading...</b>
          <ProgressBar maxValue={5000} automatic />
        </Container>
      )}
      {status === 'success' && (
        <Container display="flex" alignItems="center">
          <Icon icon="CheckmarkCircleInverted" color="accent4" margin={{ right: '8px' }} />
          <b>Upload successful</b>
        </Container>
      )}
      {status === 'error' && (
        <Container>
          <Description color="error" fontWeight="bold" lineHeight="150%" mb={0}>
            {error!.reason}
          </Description>
          <Description mb={0}>{error!.description}</Description>
        </Container>
      )}
      {status !== 'pending' && (
        <DeleteButton onClick={() => onDelete()} data-testid="delete-upload-button">
          <Icon icon="UIDelete" title="delete-image" />
        </DeleteButton>
      )}
    </PreviewContainer>
  );
};

export const ERROR_CODES = {
  'already-uploaded': { reason: 'Already uploaded', description: 'Upload a different file' },
  'file-invalid-type': { reason: 'Unsupported format', description: 'Try a JPG, PNG, or HEIC' },
  'file-too-large': { reason: 'File is too large', description: 'Must be ≤ 5MB' },
  'max-files-exceeded': { reason: 'Upload failed', description: 'Exceeded max number of files' },
  'save-failed': { reason: 'Failed to save', description: 'Try uploading again' },
  'too-many-files': { reason: 'Upload failed', description: 'Exceeded max number of files' },
  'default-code': { reason: 'Upload failed', description: 'Try a different image' },
};

export type UploadErrorCode = keyof typeof ERROR_CODES;
